import React, { useState, useRef, useEffect } from "react";
export default function Icon({
  id,
  file_name,
  icon_source,
  gridPosition,
  onPositionChange,
  isDragging,
  absolutePosition,
  isSelected,
  onSelect,
  onOpen,
  onDoubleClick,
  getContainerRect,
}) {
  const [dragStart, setDragStart] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(absolutePosition);
  const [clickTimer, setClickTimer] = useState(null);
  const iconRef = useRef(null);
  const [lastClickTime, setLastClickTime] = useState(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear any existing click timer
    if (clickTimer) {
      clearTimeout(clickTimer);
      setClickTimer(null);
    }

    // Set a timer to differentiate between click and drag
    const timer = setTimeout(() => {
      // This is a drag, not a click
      const rect = iconRef.current.getBoundingClientRect();
      const offset = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      setDragStart({ offset, startPosition: currentPosition });
      onPositionChange(id, "startDrag", currentPosition);
      setClickTimer(null);
    }, 150);

    setClickTimer(timer);
  };

  const handleMouseUp = (e) => {
    if (clickTimer) {
      clearTimeout(clickTimer);
      setClickTimer(null);

      const currentTime = Date.now();
      if (currentTime - lastClickTime < 300) {
        // Double-click detected
        onDoubleClick(id);
      } else {
        // Single click
        onSelect(id, e.ctrlKey || e.metaKey);
      }
      setLastClickTime(currentTime);
      return;
    }

    // This was a drag end
    if (!dragStart) return;

    const rect = getContainerRect ? getContainerRect() : { left: 0, top: 0 };
    const newPosition = {
      x: e.clientX - rect.left - dragStart.offset.x,
      y: e.clientY - rect.top - dragStart.offset.y,
    };

    onPositionChange(id, "endDrag", newPosition);
    setDragStart(null);
  };

  const handleMouseMove = (e) => {
    if (!dragStart) return;

    const rect = getContainerRect ? getContainerRect() : { left: 0, top: 0 };
    const newPosition = {
      x: e.clientX - rect.left - dragStart.offset.x,
      y: e.clientY - rect.top - dragStart.offset.y,
    };
    setCurrentPosition(newPosition);

    onPositionChange(id, "drag", newPosition);
  };

  const handleDragMouseUp = (e) => {
    if (!dragStart) return;

    const finalPosition = {
      x: e.clientX - dragStart.offset.x,
      y: e.clientY - dragStart.offset.y,
    };

    onPositionChange(id, "endDrag", finalPosition);
    setDragStart(null);
  };

  useEffect(() => {
    if (dragStart) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleDragMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleDragMouseUp);
      };
    }
  }, [dragStart]);

  useEffect(() => {
    if (!dragStart && !isDragging) {
      setCurrentPosition(absolutePosition);
    }
  }, [absolutePosition, isDragging, dragStart]);

  const iconStyle = {
    position: "absolute",
    left: `${currentPosition.x}px`,
    top: `${currentPosition.y}px`,
    minWidth: "80px",
    minHeight: "80px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "default",
    userSelect: "none",
    zIndex: isDragging ? 1000 : 1,
    transform: isDragging ? "scale(1.1)" : "scale(1)",
    opacity: isDragging ? 0.4 : 1,
    filter: isDragging ? "drop-shadow(0 4px 8px rgba(0,0,0,1))" : "none",
    transition: "none", //isDragging ? "none" : "all 0.2s ease",
    backgroundColor: isSelected ? "#ffffff3b" : "transparent",
    //border: isSelected ? "2px solid #98caff4d" : "2px solid transparent",
    borderRadius: "4px",
    "--icon-hover-color": "#ffffff17",
  };

  return (
    <div
      ref={iconRef}
      style={iconStyle}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onDoubleClick={onOpen}
      className="icon-container"
    >
      <div
        style={{
          width: "50px",
          height: "50px",
          marginBottom: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <img
          src={icon_source}
          alt={`${file_name} icon`}
          style={{
            width: "45px",
            height: "45px",
            pointerEvents: "none",
            objectFit: "contain",
          }}
        />
      </div>
      <div
        style={{
          display: isSelected || isDragging ? "flex" : "block",
          justifyContent: isSelected || isDragging ? "center" : "start",
          alignItems: "center",
          fontSize: "12px",
          textAlign: "center",
          color: "#ffffffff",
          maxWidth: "76px",
          textOverflow: "ellipsis",
          overflow: isSelected || isDragging ? "visible" : "hidden",
          wordBreak: isSelected || isDragging ? "break-all" : "",
        }}
      >
        {file_name}
      </div>
    </div>
  );
}
